import {
    AppEnum,
    FaCalendar,
    FaDollarSign,
    FaHashtag,
    FaUser,
    FaUserTag,
    Header,
    MandateEnums,
    OptionHelper,
    RecordMap
} from "@/Shared";

import { UserModel, ContactModel } from '@/Domains';

import { MandateViewModel } from "../Logic/ViewModels";

export type SelectOption = {
    value: any,
    label: any
}


/**
 * Class: MandateHeader
 *
 * Description:
 * Utility class responsible for building table headers for the Mandate list view.
 * Each method returns a configuration object describing:
 * - Column key and label
 * - Icon for UI display
 * - Type of data
 * - Sorting capabilities
 * - Optional filter settings for dropdown filtering
 *
 * Responsibilities:
 * - Centralize header definitions for consistency across the UI
 * - Provide dynamic filter options based on domain data (contacts, agents, enums)
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */
export class MandateHeader {

    /** Private constructor to enforce usage of static build method */
    private constructor() { }

    /** ID column definition */
    getId() {
        return {
            key: 'id',
            icon: <FaHashtag />,
            label: 'ID',
            type: 'number',
            isSortable: true
        }
    }

    /**
     * Returns the operation type column definition
     * @param operationTypes Map of operation types to populate filter
     */
    getOperationType(oprationTypes: AppEnum[]) {
        return {
            key: 'operationType',
            icon: <FaUserTag />,
            label: 'Nature mandat',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Nature",
                options: oprationTypes,
                case: 'sensitive'
            }
        }
    }

    /** Fee rate column */
    getFeeRate() {
        return {
            key: 'feeRate',
            icon: <FaUserTag />,
            label: 'Honoraires (%)',
            type: 'number',
            isSortable: true,
        }
    }

    /** Start date column */
    getStartAt() {
        return {
            key: 'startAt',
            icon: <FaCalendar />,
            label: 'Date Début',
            type: 'date',
            isSortable: true
        }
    }

    /** End date column */
    getEndAt() {
        return {
            key: 'endAt',
            icon: <FaCalendar />,
            label: 'Date Fin',
            type: 'date',
            isSortable: true
        }
    }

    /**
     * Property type column
     * @param types Map of property types for filtering
     */
    getType(types: AppEnum[]) {
        return {
            key: 'propertyType',
            icon: <FaUserTag />,
            label: 'Type de bien',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Type",
                options: types,
                case: 'sensitive'
            }
        }
    }

    /**
     * Property category column
     * @param categoryTypes Map of property categories for filtering
     */
    getCategory(categoryTypes: AppEnum[]) {
        return {
            key: 'propertyCategory',
            icon: <FaUserTag />,
            label: 'Catégorie de bien',
            type: 'string',
            isSortable: true,
            isHidden: true,
            filter: {
                label: "Catégorie",
                options: categoryTypes,
                case: 'sensitive'
            }
        }
    }


    getFurnished() {
        return {
            key: 'furnished',
            icon: <FaUser />,
            label: 'Meublé',
            type: 'string',
            isSortable: true
        }
    }

    /**
     * Buyer/Owner column
     * @param contactMap Map of contacts to populate filter options
     */
    getBuyer(contactMap: RecordMap<ContactModel>) {
        return {
            key: 'contact',
            icon: <FaUserTag />,
            label: 'Propriétaire',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Propriétaire",
                options: OptionHelper.ownerOptions(contactMap.toArray()),
                case: 'sensitive'
            }
        }
    }

    /**
     * Referent/Agent column
     * @param agents Map of agents to populate filter options
     */
    getReferent(agents: RecordMap<UserModel>) {
        return {
            key: 'agent',
            icon: <FaUser />,
            label: 'Conclu par',
            type: 'string',
            isSortable: true,
            isHidden: false,
            filter: {
                label: "Conclu Par",
                case: 'sensitive',
                options: OptionHelper.userOptions(agents.toArray())
            }
        }
    }

    /**
    * Availability column
    * @param availabilityMap Map of availability statuses for filter
    */
    getAvailability(availabilityMap: AppEnum[]) {
        return {
            key: 'availability',
            icon: <FaUserTag />,
            label: 'Disponibilité',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Disponibilité",
                options: availabilityMap,
                case: 'sensitive'
            }
        }
    }

    /** Price column */
    getPrice() {
        return {
            key: 'price',
            icon: <FaDollarSign />,
            label: 'Prix',
            type: 'numeric',
            isSortable: true
        }
    }

    /**
     * Static factory to build a full header record for MandateViewModel
     *
     * @param contactMap Map of contacts
     * @param agentMap Map of agents
     * @param viewEnums Mandate enums for operation types
     * @returns Record mapping MandateViewModel keys to Header configurations
     */
    static build(
        contactMap: RecordMap<ContactModel>,
        agentMap: RecordMap<UserModel>,
        viewEnums: MandateEnums
    ): Record<keyof MandateViewModel, Header<MandateViewModel>> {

        const header = new MandateHeader();

        return {
            id: header.getId(),
            operationType: header.getOperationType(viewEnums.OperationTypeEnum),
            feeRate: header.getFeeRate(),
            startAt: header.getStartAt(),
            endAt: header.getEndAt(),
            agent: header.getReferent(agentMap),
        } as Record<keyof MandateViewModel, Header<MandateViewModel>>
    }
}